import { useEffect } from 'react'

/**
 * AccessibilityEnhancer component that ensures external widgets are keyboard accessible.
 * This component specifically targets the Help Center widget and adds necessary
 * accessibility attributes for keyboard navigation and proper landmarks.
 */
const AccessibilityEnhancer = () => {
  useEffect(() => {
    // CRITICAL: Remove invalid roles from body/html elements
    // These should NEVER have ARIA roles as they are not allowed per ARIA spec
    // Shared utility function used throughout the component
    const removeInvalidBodyRoles = () => {
      const { body } = document
      const { documentElement: html } = document

      if (body && body.hasAttribute('role')) {
        body.removeAttribute('role')
        body.removeAttribute('aria-label')
      }

      if (html && html.hasAttribute('role')) {
        html.removeAttribute('role')
        html.removeAttribute('aria-label')
      }
    }

    const handleKeyboardNavigation = (event: KeyboardEvent) => {
      const { key } = event
      const target = event.currentTarget as HTMLElement
      const scrollAmount = 50 // pixels

      switch (key) {
        case 'ArrowUp':
          event.preventDefault()
          target.scrollTop -= scrollAmount
          break
        case 'ArrowDown':
          event.preventDefault()
          target.scrollTop += scrollAmount
          break
        case 'Home':
          event.preventDefault()
          target.scrollTop = 0
          break
        case 'End':
          event.preventDefault()
          target.scrollTop = target.scrollHeight
          break
        case 'PageUp':
          event.preventDefault()
          target.scrollTop -= target.clientHeight
          break
        case 'PageDown':
          event.preventDefault()
          target.scrollTop += scrollAmount
          break
        default:
          // Let other keys pass through
          break
      }
    }

    const enhanceElevioWidget = () => {
      // Add landmarks to Elevio widget content to fix accessibility issues
      // Using semantic selectors instead of class names for better robustness

      // Always run cleanup first to remove any invalid roles from body/html
      removeInvalidBodyRoles()

      // Clean up any duplicate or nested landmarks first
      const cleanupDuplicateLandmarks = () => {
        // Remove duplicate contentinfo landmarks
        const footers = document.querySelectorAll('[role="contentinfo"][aria-label*="Help Center"]')
        if (footers.length > 1) {
          // Keep only the first one, remove others
          for (let i = 1; i < footers.length; i += 1) {
            footers[i].removeAttribute('role')
            footers[i].removeAttribute('aria-label')
          }
        }

        // Fix nested landmarks - remove role from nested elements
        const nestedLandmarks = document.querySelectorAll(
          '[role="contentinfo"] [role="contentinfo"], [role="main"] [role="main"], [role="navigation"] [role="navigation"]'
        )
        nestedLandmarks.forEach((nested) => {
          nested.removeAttribute('role')
          nested.removeAttribute('aria-label')
        })
      }

      cleanupDuplicateLandmarks()

      // Check if we already have landmarks to avoid duplicates
      const existingFooters = document.querySelectorAll(
        '[role="contentinfo"][aria-label*="Help Center"]'
      )
      const hasHelpCenterFooter = existingFooters.length > 0

      // Strategy 1: Find elements by their content and structure
      // Look for the Help Center title by searching for text content
      const allDivs = document.querySelectorAll('div')
      allDivs.forEach((div) => {
        const text = div.textContent?.trim()

        // Identify Help Center header by content
        // Only add if not already inside a landmark
        if (
          text === 'Help Center' &&
          !div.hasAttribute('role') &&
          !div.closest('[role="banner"], [role="main"], [role="navigation"], [role="contentinfo"]')
        ) {
          div.setAttribute('role', 'banner')
          div.setAttribute('aria-label', 'Help Center Header')
        }

        // Identify footer by Elevio branding - but only add one footer
        // Check that it's not nested inside another landmark
        // IMPORTANT: Also check if this is the #_elev_io widget itself
        const isElevioWidget = div.id === '_elev_io' || div.classList.contains('_elevio_widget')

        if (
          !hasHelpCenterFooter &&
          text?.includes('Elevio') &&
          text?.includes('Dixa') &&
          !div.hasAttribute('role') &&
          !div.closest('[role="contentinfo"], [role="main"], [role="navigation"]') &&
          !isElevioWidget // Don't add role to the main Elevio widget container
        ) {
          div.setAttribute('role', 'contentinfo')
          div.setAttribute('aria-label', 'Help Center Footer')
        }
      })

      // Strategy 2: Find search inputs by their attributes
      const searchInputs = document.querySelectorAll(
        'input[type="search"], input[placeholder*="search" i], input[name*="search" i]'
      )
      searchInputs.forEach((input) => {
        const container = input.closest('div')
        // Only add search role if not already in a landmark
        if (
          container &&
          !container.hasAttribute('role') &&
          !container.closest('[role="search"], [role="main"], [role="navigation"]')
        ) {
          container.setAttribute('role', 'search')
          container.setAttribute('aria-label', 'Help Center Search')
        }
      })

      // WCAG 3.3.2 Fix: Add accessible label to unlabeled input in Help Center widget
      // Elevio uses name="username" for its search input; it is not a credentials field.
      const usernameInputs = document.querySelectorAll('input[name="username"]')
      usernameInputs.forEach((input) => {
        if (
          !input.hasAttribute('aria-label') &&
          !input.hasAttribute('aria-labelledby') &&
          (!input.id || !document.querySelector(`label[for="${input.id}"]`))
        ) {
          input.setAttribute('aria-label', 'Search help center')
        }
      })

      // WCAG 4.1.2 Fix: Add accessible names to menuitem elements in Help Center widget
      const menuitems = document.querySelectorAll('[role="menuitem"]')
      menuitems.forEach((menuitem) => {
        // Only add label if it doesn't already have one
        if (!menuitem.hasAttribute('aria-label') && !menuitem.hasAttribute('aria-labelledby')) {
          // Try to use the text content as the label
          const text = menuitem.textContent?.trim()
          if (text) {
            menuitem.setAttribute('aria-label', text)
          } else {
            // Fallback if no text content
            menuitem.setAttribute('aria-label', 'Menu option')
          }
        }
      })

      // Strategy 3: Find navigation areas by looking for groups of interactive elements
      // Look for containers with multiple clickable items that might be menu items
      const findNavigationContainers = () => {
        const containers = document.querySelectorAll('div')
        containers.forEach((container) => {
          // Skip if already has a role or is inside another landmark
          if (
            container.hasAttribute('role') ||
            container.closest('[role="navigation"], [role="main"], [role="contentinfo"]')
          ) {
            return
          }

          // Check if this container has multiple clickable children (likely menu items)
          const clickableElements = container.querySelectorAll(
            'button, [role="button"], a[href], [tabindex="0"]'
          )
          const directClickableChildren = Array.from(clickableElements).filter(
            (el) => el.parentElement === container
          )

          // If we have multiple clickable items and no role set, it's likely navigation
          if (directClickableChildren.length >= 3) {
            // Additional check: see if these items have similar structure (likely menu items)
            const firstItemText = directClickableChildren[0]?.textContent?.trim()
            if (
              firstItemText &&
              (firstItemText.includes('Support') ||
                firstItemText.includes('Docs') ||
                firstItemText.includes('Training') ||
                firstItemText.includes('Help'))
            ) {
              container.setAttribute('role', 'navigation')
              container.setAttribute('aria-label', 'Help Center Navigation')
            }
          }
        })
      }
      findNavigationContainers()

      // Strategy 4: Find main content area by looking for scrollable regions
      const scrollableRegions = document.querySelectorAll('[style*="overflow"], [style*="scroll"]')
      scrollableRegions.forEach((region) => {
        // Check if this looks like a main content area (has substantial content)
        const hasSubstantialContent = region.querySelectorAll('*').length > 10
        if (
          hasSubstantialContent &&
          !region.hasAttribute('role') &&
          !region.closest('[role="main"]')
        ) {
          region.setAttribute('role', 'main')
          region.setAttribute('aria-label', 'Help Center Main Content')
        }
      })

      // Strategy 5: Mark the main widget container as complementary (not contentinfo to avoid duplicates)
      // Find the outermost help widget container
      const floatingElements = document.querySelectorAll('[style*="fixed"], [style*="absolute"]')
      floatingElements.forEach((element) => {
        // Check if this is a substantial container (not just a small icon or button)
        const rect = element.getBoundingClientRect()
        const isSubstantial = rect.width > 200 && rect.height > 200

        // Only add role if not already set and not inside another landmark
        if (
          isSubstantial &&
          !element.hasAttribute('role') &&
          !element.closest('[role="main"], [role="complementary"]')
        ) {
          // Check if it has help-related content
          const hasHelpContent = Array.from(element.querySelectorAll('*')).some(
            (el) => el.textContent?.includes('Help') || el.textContent?.includes('Support')
          )

          if (hasHelpContent) {
            // Mark as complementary (sidebar) rather than another contentinfo
            // This avoids duplicate footer landmarks
            element.setAttribute('role', 'complementary')
            element.setAttribute('aria-label', 'Help Center Widget')
          }
        }
      })
    }

    const enhanceScrollableRegions = () => {
      // Find scrollable regions by their style properties instead of class names
      const scrollableElements = document.querySelectorAll(
        '[style*="overflow-y"], [style*="overflow: auto"], [style*="overflow: scroll"]'
      )

      scrollableElements.forEach((element) => {
        // Additional check to ensure this is part of the help widget
        const isHelpWidget =
          element.closest('[style*="fixed"]') ||
          element.closest('[style*="absolute"]') ||
          Array.from(element.querySelectorAll('*')).some(
            (el) =>
              el.textContent?.includes('Help') ||
              el.textContent?.includes('Support') ||
              el.textContent?.includes('Docs')
          )

        if (isHelpWidget && !element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '0')
          element.setAttribute('role', 'region')
          element.setAttribute('aria-label', 'Help Center content')

          // Remove existing listeners to prevent duplicates
          element.removeEventListener('keydown', handleKeyboardNavigation as EventListener)
          // Add keyboard navigation
          element.addEventListener('keydown', handleKeyboardNavigation as EventListener)
        }
      })

      // Also enhance the Elevio widget with proper landmarks
      enhanceElevioWidget()
    }

    // Observer to detect when external widgets are loaded
    const observer = new MutationObserver((mutations) => {
      // Look for any substantial DOM changes that might be a widget
      const relevantMutation = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element

            // Detection strategy: Look for elements that might be help widgets
            // 1. Fixed or absolute positioned elements (modals/overlays)
            const isPositioned = element.matches('[style*="fixed"], [style*="absolute"]')

            // 2. Elements with help-related content
            const hasHelpContent =
              element.textContent?.toLowerCase().includes('help') ||
              element.textContent?.toLowerCase().includes('support') ||
              element.textContent?.toLowerCase().includes('docs')

            // 3. Search inputs
            const hasSearchInput = element.querySelector(
              'input[type="search"], input[placeholder*="search" i]'
            )

            // 4. Multiple interactive elements (likely a widget with menu items)
            const interactiveElements = element.querySelectorAll(
              'button, a[href], [role="button"], [tabindex]'
            )
            const hasMultipleInteractive = interactiveElements.length > 2

            // 5. Scrollable regions
            const hasScrollable = element.querySelector('[style*="overflow"]')

            // If any of these conditions are met, it might be the help widget
            return (
              isPositioned ||
              hasHelpContent ||
              hasSearchInput ||
              hasMultipleInteractive ||
              hasScrollable
            )
          }
          return false
        })
      )

      if (relevantMutation) {
        // CRITICAL: Always clean up invalid body roles first when DOM changes
        // This prevents the modal from triggering invalid role assignments
        removeInvalidBodyRoles()

        // Use setTimeout to ensure DOM is fully updated
        setTimeout(enhanceScrollableRegions, 100)
        // Run enhancement again after a longer delay to catch late-rendered content
        setTimeout(enhanceElevioWidget, 500)
      }
    })

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    // Initial enhancement
    enhanceScrollableRegions()
    enhanceElevioWidget()
    // Run again after a delay to catch any late-loading widgets
    setTimeout(enhanceScrollableRegions, 1000)
    setTimeout(enhanceElevioWidget, 1500)

    return () => {
      observer.disconnect()
      // Clean up event listeners from any elements we enhanced
      const enhancedElements = document.querySelectorAll('[aria-label="Help Center content"]')
      enhancedElements.forEach((element) => {
        element.removeEventListener('keydown', handleKeyboardNavigation as EventListener)
      })
    }
  }, [])

  return null
}

export default AccessibilityEnhancer
